/**
 * LessonSound.js — the /learn lesson sound board (Task #251).
 *
 * A tiny, self-contained UI sound-effects player for the Brilliant-style lesson
 * stage. It plays the 29 authored cues in `static/audio/learn/brilliant/<group>/`
 * by semantic name (tapOn, correct, xpTally…), supports gap-separated sequences,
 * and is mute-aware (persisted).
 *
 * IMPORTANT — this is NOT the sacred audio pipeline. It plays short UI cues
 * through isolated HTMLAudioElement clones, never the shared TTS AudioContext,
 * the A2F bridge, or the resident legend's GreetingService. It owns no 3D, never
 * pauses or reconfigures anything else, and fails silent on any error. Browser
 * autoplay policy blocks sound until a user gesture, so it auto-unlocks on the
 * first pointer/key gesture (and `unlock()` can be called explicitly from the
 * lesson "Begin" click).
 */

const BASE = "/static/audio/learn/brilliant/";

// Semantic name -> file (relative to BASE). Grouped to mirror the asset folders.
const MAP = {
  // 1 — interaction
  tapOn: "1-interaction/toggle-on.mp3",
  tapOff: "1-interaction/toggle-off.mp3",
  choice: "1-interaction/lightweight-choice.mp3",
  highlight: "1-interaction/highlight-node.mp3",
  dragStart: "1-interaction/drag-started.mp3",
  dragRelease: "1-interaction/drag-released.mp3",
  hint: "1-interaction/hint.mp3",
  skip: "1-interaction/skip.mp3",
  start: "1-interaction/start-lesson.mp3",
  continue: "1-interaction/continue.mp3",
  // 2 — answer feedback
  correct: "2-answer-feedback/correct.mp3",
  incorrect: "2-answer-feedback/incorrect.mp3",
  lessonFailed: "2-answer-feedback/lesson-failed.mp3",
  // 3 — xp & complete
  xpStart: "3-xp-and-complete/tutor-xp-countup-start.mp3",
  xpStop: "3-xp-and-complete/tutor-xp-countup-stop.mp3",
  xpTally: "3-xp-and-complete/xp-tally.mp3",
  xpTally1: "3-xp-and-complete/xp-tally-1.mp3",
  xpTally2: "3-xp-and-complete/xp-tally-2.mp3",
  xpTally3: "3-xp-and-complete/xp-tally-3.mp3",
  xpTally4: "3-xp-and-complete/xp-tally-4.mp3",
  xpTally5: "3-xp-and-complete/xp-tally-5.mp3",
  xpTallyFinal: "3-xp-and-complete/xp-tally-final.mp3",
  endstate: "3-xp-and-complete/endstate.mp3",
  courseComplete: "3-xp-and-complete/course-complete.mp3",
  // 4 — leagues
  leaguesUnlocked: "4-leagues/leagues-unlocked-partone.mp3",
  leaguesPlacement: "4-leagues/leagues-placement-medium.mp3",
  welcomeLeagues: "4-leagues/welcome-to-leagues.mp3",
  welcomeLeaguesEarn: "4-leagues/welcome-to-leagues-earn-xp-to-get-ahead.mp3",
  welcomeLeaguesLevelUp: "4-leagues/welcome-to-leagues-level-up-each-week.mp3",
};

// Cues to warm as soon as audio is unlocked — the ones a learner hears first.
const CORE = ["tapOn", "tapOff", "choice", "correct", "incorrect", "continue", "hint"];

const MUTE_KEY = "legendlabs.learnSound"; // "on" | "off"

class LessonSound {
  constructor() {
    this._unlocked = false;
    this._templates = Object.create(null); // name -> primed HTMLAudioElement
    this._muted = this._readMuted();
    this._gestureBound = false;
    this._bindFirstGesture();
  }

  /* ---------------- mute ---------------- */
  _readMuted() {
    try { return localStorage.getItem(MUTE_KEY) === "off"; } catch (_) { return false; }
  }
  isMuted() { return this._muted; }
  setMuted(on) {
    this._muted = !!on;
    try { localStorage.setItem(MUTE_KEY, this._muted ? "off" : "on"); } catch (_) {}
  }
  toggleMuted() { this.setMuted(!this._muted); return this._muted; }

  /* ---------------- unlock ---------------- */
  // Auto-unlock on the first real gesture so cues work without an explicit call.
  _bindFirstGesture() {
    if (this._gestureBound || typeof window === "undefined") return;
    this._gestureBound = true;
    const handler = () => { this.unlock(); };
    try {
      window.addEventListener("pointerdown", handler, { once: true, capture: true });
      window.addEventListener("keydown", handler, { once: true, capture: true });
      window.addEventListener("touchstart", handler, { once: true, capture: true });
    } catch (_) {}
  }

  // Bless playback inside a user gesture and warm the core cues. Idempotent.
  unlock() {
    if (this._unlocked) return;
    this._unlocked = true;
    // Warm the first-heard cues immediately, then ALL remaining cues so later
    // events (xp-tally combo, leagues voice, lesson-failed, completion stack)
    // never lag or drop on first play.
    try { CORE.forEach((n) => this._template(n)); } catch (_) {}
    try { Object.keys(MAP).forEach((n) => this._template(n)); } catch (_) {}
  }

  /* ---------------- playback ---------------- */
  _template(name) {
    const rel = MAP[name];
    if (!rel) return null;
    let a = this._templates[name];
    if (!a) {
      a = new Audio(BASE + rel);
      a.preload = "auto";
      a.crossOrigin = "anonymous";
      try { a.load(); } catch (_) {}
      this._templates[name] = a;
    }
    return a;
  }

  // Play a named cue. Clones the primed element so rapid repeats overlap cleanly
  // and never cut each other off. Returns the playing element (or null).
  play(name, opts = {}) {
    if (this._muted) return null;
    const tmpl = this._template(name);
    if (!tmpl) return null;
    try {
      const node = tmpl.cloneNode(true);
      node.muted = false;
      node.volume = typeof opts.volume === "number" ? Math.max(0, Math.min(1, opts.volume)) : 1;
      if (typeof opts.rate === "number") {
        node.playbackRate = opts.rate;
        try { node.preservesPitch = false; node.mozPreservesPitch = false; node.webkitPreservesPitch = false; } catch (_) {}
      }
      const p = node.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      return node;
    } catch (_) {
      return null;
    }
  }

  // Play a list of cues in order, separated by `gapMs`. Names may be objects
  // ({name, volume, rate}) for per-step control. Best-effort; returns a promise.
  async sequence(names, gapMs = 220) {
    for (let i = 0; i < names.length; i++) {
      const step = names[i];
      if (typeof step === "string") this.play(step);
      else if (step && step.name) this.play(step.name, step);
      if (i < names.length - 1) await new Promise((r) => setTimeout(r, gapMs));
    }
  }

  // XP tally count-up: the 5 pre-pitched tally cues rise per combo; `n` (1-based)
  // picks the step, clamped, ending on the brighter final cue past the ramp.
  tallyStep(n) {
    const i = Math.max(1, Math.min(5, n | 0));
    this.play("xpTally" + i);
  }

  /* ---------------- chalk foley (Task #348) ---------------- */
  // Runtime-OWNED chalk strokes under the board's write/draw reveals. Authors
  // never control sound — the write verb triggers this, nothing else. There is
  // no shipped asset: three short stroke variants are synthesized ONCE into
  // tiny WAV data URIs (band-limited noise + hand-motion envelope), then played
  // through the SAME isolated HTMLAudio clone path as every other UI cue —
  // never the sacred narration pipeline, never a second live AudioContext.
  _chalkTemplate(i) {
    const key = "chalk" + i;
    let a = this._templates[key];
    if (!a) {
      try {
        a = new Audio(chalkWavUri(0xC4A1C + i * 977));
        a.preload = "auto";
        this._templates[key] = a;
      } catch (_) { return null; }
    }
    return a;
  }

  // One chalk stroke. volume ~0.2–0.3 sits under the narration nicely; rate
  // jitter keeps repeated strokes from sounding stamped.
  chalk(opts = {}) {
    if (this._muted) return null;
    const i = typeof opts.variant === "number"
      ? Math.abs(opts.variant | 0) % 3
      : Math.floor(Math.random() * 3);
    const tmpl = this._chalkTemplate(i);
    if (!tmpl) return null;
    try {
      const node = tmpl.cloneNode(true);
      node.muted = false;
      node.volume = typeof opts.volume === "number" ? Math.max(0, Math.min(1, opts.volume)) : 0.24;
      const rate = typeof opts.rate === "number" ? opts.rate : 0.92 + Math.random() * 0.18;
      node.playbackRate = rate;
      try { node.preservesPitch = false; node.mozPreservesPitch = false; node.webkitPreservesPitch = false; } catch (_) {}
      const p = node.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      return node;
    } catch (_) {
      return null;
    }
  }
}

/* ---------------- chalk stroke synthesis (module-private) ---------------- */
// Deterministic PRNG so each variant sounds identical on every load.
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// A chalk stroke ≈ band-limited noise (≈1.2–4.5 kHz) shaped by a hand-motion
// envelope: quick attack, wobbling sustain (the dragging hand), soft lift-off,
// with fine grain dropouts for tooth. ~320 ms mono 16-bit @ 22050 Hz ≈ 14 KB.
function chalkWavUri(seed) {
  const rng = mulberry32(seed);
  const fs = 22050;
  const dur = 0.26 + rng() * 0.1;
  const n = Math.floor(fs * dur);
  const lpA = 1 - Math.exp((-2 * Math.PI * 4500) / fs);
  const hpA = 1 - Math.exp((-2 * Math.PI * 1300) / fs);
  const wobbleHz = 6 + rng() * 5;
  const wobblePh = rng() * Math.PI * 2;
  let lp1 = 0, lp2 = 0, grain = 0.8;
  const smp = new Float32Array(n);
  let peak = 1e-6;
  for (let i = 0; i < n; i++) {
    const x = rng() * 2 - 1;
    lp1 += lpA * (x - lp1);          // cut the hiss above ~4.5 kHz
    lp2 += hpA * (lp1 - lp2);        // remove the rumble below ~1.3 kHz
    const band = lp1 - lp2;
    const t = i / n;
    const attack = Math.min(1, (i / fs) / 0.012);
    const release = Math.min(1, ((n - i) / fs) / 0.05);
    const wobble = 0.75 + 0.25 * Math.sin(2 * Math.PI * wobbleHz * (i / fs) + wobblePh);
    if ((i & 63) === 0) grain = 0.55 + 0.45 * rng(); // chalk tooth
    const v = band * attack * release * wobble * grain * (1 - 0.25 * t);
    smp[i] = v;
    const av = Math.abs(v);
    if (av > peak) peak = av;
  }
  const gain = 0.85 / peak;
  const bytes = new Uint8Array(44 + n * 2);
  const dv = new DataView(bytes.buffer);
  const w32 = (o, v) => dv.setUint32(o, v, true);
  const w16 = (o, v) => dv.setUint16(o, v, true);
  w32(0, 0x46464952); w32(4, 36 + n * 2); w32(8, 0x45564157);   // RIFF….WAVE
  w32(12, 0x20746D66); w32(16, 16); w16(20, 1); w16(22, 1);      // fmt  PCM mono
  w32(24, fs); w32(28, fs * 2); w16(32, 2); w16(34, 16);         // rates, 16-bit
  w32(36, 0x61746164); w32(40, n * 2);                           // data
  for (let i = 0; i < n; i++) {
    dv.setInt16(44 + i * 2, Math.max(-32768, Math.min(32767, Math.round(smp[i] * gain * 32767))), true);
  }
  let bin = "";
  for (let i = 0; i < bytes.length; i += 8192) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + 8192, bytes.length)));
  }
  return "data:audio/wav;base64," + btoa(bin);
}

const lessonSound = new LessonSound();
if (typeof window !== "undefined") window.lessonSound = lessonSound;
export default lessonSound;
export { LessonSound };
